import React from 'react';
import { Cpu, Activity, Zap, Thermometer, Clock, BarChart3 } from 'lucide-react';

const gpuList = [
  { id: 'GPU-0', name: 'NVIDIA L40S', memory: '48 GB', usage: 72, temp: 68, power: 245, status: 'active' },
  { id: 'GPU-1', name: 'NVIDIA L40S', memory: '48 GB', usage: 45, temp: 62, power: 180, status: 'active' },
  { id: 'GPU-2', name: 'NVIDIA A100', memory: '80 GB', usage: 0, temp: 35, power: 15, status: 'idle' },
  { id: 'GPU-3', name: 'NVIDIA A100', memory: '80 GB', usage: 0, temp: 34, power: 15, status: 'idle' },
];

function GpuSection() {
  return (
    <div className="p-8 space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-white">GPU Resources</h2>
        <p className="text-slate-400 mt-1">Monitor and manage GPU compute nodes</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="glass-panel p-6">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Total GPUs</p>
          <p className="text-3xl font-bold text-white mt-2">4</p>
          <p className="text-xs text-slate-500 mt-1">2 active · 2 idle</p>
        </div>
        <div className="glass-panel p-6">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Total VRAM</p>
          <p className="text-3xl font-bold text-white mt-2">256 GB</p>
          <p className="text-xs text-slate-500 mt-1">117 GB used · 139 GB free</p>
        </div>
        <div className="glass-panel p-6">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Power Draw</p>
          <p className="text-3xl font-bold text-white mt-2">495 W</p>
          <p className="text-xs text-slate-500 mt-1">30% of total capacity</p>
        </div>
      </div>

      {/* GPU List */}
      <div className="glass-panel divide-y divide-slate-800/50">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Cpu className="w-5 h-5 text-purple-400" />
            GPU Nodes
          </h3>
        </div>
        {gpuList.map((gpu) => (
          <div key={gpu.id} className="p-6 hover:bg-slate-800/30 transition-colors">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${gpu.status === 'active' ? 'bg-emerald-500' : 'bg-slate-600'}`} />
                <div>
                  <p className="text-white font-semibold">{gpu.name}</p>
                  <p className="text-xs text-slate-500">{gpu.id} · {gpu.memory} VRAM</p>
                </div>
              </div>
              <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                gpu.status === 'active' ? 'bg-emerald-600/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
              }`}>
                {gpu.status.toUpperCase()}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Activity className="w-3 h-3" /> Usage
                  </span>
                  <span className="text-xs text-white">{gpu.usage}%</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all bg-gradient-to-r from-emerald-500 via-amber-500 to-red-500" style={{ width: `${gpu.usage}%` }} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Thermometer className="w-4 h-4 text-red-400" />
                <span className="text-sm text-slate-300">{gpu.temp}°C</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" />
                <span className="text-sm text-slate-300">{gpu.power}W</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GpuSection;
